import React, { useState } from 'react';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

function pct(p) { return `${Math.round((p || 0) * 100)}%`; }
function pct1(p) { return `${((p || 0) * 100).toFixed(1)}%`; }

function Avatar({ team, size = 22 }) {
  const initial = (team?.displayName || '?').trim().charAt(0).toUpperCase();
  if (team?.avatarUrl) {
    return (
      <img src={team.avatarUrl} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E5E5EA' }} onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: '#E5E5EA', color: '#6E6E73', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700 }}>{initial}</div>
  );
}

// Standard tournament seeding order for a bracket of power-of-two size.
function seedOrder(size) {
  let order = [1];
  while (order.length < size) {
    const n = order.length * 2 + 1;
    const next = [];
    for (const s of order) { next.push(s); next.push(n - s); }
    order = next;
  }
  return order;
}

const COLUMN_META = [
  { key: 'championshipProbability', label: 'Champion' }, // depth 0 (1 team)
  { key: 'finalsProbability', label: 'Final' },          // depth 1 (2 teams)
  { key: 'semifinalProbability', label: 'Semifinal' },   // depth 2 (4 teams)
  { key: 'playoffProbability', label: 'Wild Card' },     // depth 3+ (8 teams)
];
function metaForDepth(depth) { return COLUMN_META[Math.min(depth, COLUMN_META.length - 1)]; }

const CARD_W = 152;
const CARD_H = 50;
const X_PITCH = CARD_W + 48;

export default function PlayoffBracket({ bootstrap, results, myRosterId, onSelectTeam }) {
  const [hover, setHover] = useState(null); // {x, y, stage, metaKey, picks} — styled slot tooltip
  if (!bootstrap || !results) return null;
  const N = bootstrap.playoffTeams || 6;
  const playoffWeeks = bootstrap.playoffWeeks || [];

  // Projected seeds: order by how often each team makes the playoffs.
  const seeded = [...bootstrap.teams]
    .map(t => ({ team: t, result: results.teams[t.rosterId] }))
    .filter(x => x.result)
    .sort((a, b) => b.result.playoffProbability - a.result.playoffProbability);

  const field = seeded.slice(0, N);
  const outside = seeded.slice(N);
  const champ = [...seeded].sort((a, b) => b.result.championshipProbability - a.result.championshipProbability)[0];

  // ── Build the bracket as columns of nodes (column 0 = leaves) ──
  let pow2 = 1;
  while (pow2 < N) pow2 *= 2;
  const totalRounds = Math.log2(pow2);
  const order = seedOrder(pow2); // seed numbers placed in bracket order
  const leaves = order.map(s => (s <= N ? field[s - 1] : null)); // null = bye

  // teamsUnder[c][k] = list of {team,result} that could occupy node k of column c
  const teamsUnder = [leaves.map(l => (l ? [l] : []))];
  for (let c = 1; c <= totalRounds; c++) {
    const prev = teamsUnder[c - 1];
    const cur = [];
    for (let k = 0; k < prev.length / 2; k++) cur.push([...prev[2 * k], ...prev[2 * k + 1]]);
    teamsUnder.push(cur);
  }

  // Vertical geometry
  const PAIR_GAP = 24;
  const leafY = leaves.map((_, i) => {
    const pair = Math.floor(i / 2);
    const within = i % 2;
    return pair * (2 * CARD_H + 6 + PAIR_GAP) + within * (CARD_H + 6);
  });
  const centerY = [leafY.map(y => y + CARD_H / 2)];
  for (let c = 1; c <= totalRounds; c++) {
    const prev = centerY[c - 1];
    const cur = [];
    for (let k = 0; k < prev.length / 2; k++) cur.push((prev[2 * k] + prev[2 * k + 1]) / 2);
    centerY.push(cur);
  }
  const totalH = leafY[leafY.length - 1] + CARD_H;
  const totalW = (totalRounds + 1) * X_PITCH;

  const xFor = c => c * X_PITCH;

  // Connector paths (drawn behind cards)
  const connectors = [];
  for (let c = 1; c <= totalRounds; c++) {
    const childX = xFor(c - 1) + CARD_W;
    const parentX = xFor(c);
    const midX = childX + (parentX - childX) / 2;
    centerY[c].forEach((py, k) => {
      [2 * k, 2 * k + 1].forEach(j => {
        const cy = centerY[c - 1][j];
        connectors.push(`M ${childX} ${cy} H ${midX} V ${py} H ${parentX}`);
      });
    });
  }

  // Column headers with the week that round is played. Anchor to the END of the
  // playoff weeks — the championship is always the last playoff week — so the
  // labels stay correct no matter how many playoff weeks a league runs. The
  // champion column has no game of its own (it's the winner of the final).
  const headerFor = c => {
    const meta = metaForDepth(totalRounds - c);
    let week = null;
    if (c < totalRounds) {
      const idx = playoffWeeks.length - totalRounds + c;
      if (idx >= 0 && idx < playoffWeeks.length) week = playoffWeeks[idx];
    }
    return { label: meta.label, week };
  };

  function displayedFor(c, k) {
    const cands = teamsUnder[c][k];
    if (!cands.length) return { picks: [], metaKey: metaForDepth(totalRounds - c).key };
    const metaKey = metaForDepth(totalRounds - c).key;
    const sorted = [...cands].sort((a, b) => (b.result[metaKey] || 0) - (a.result[metaKey] || 0));
    return { picks: sorted, metaKey };
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '20px 24px 24px', fontFamily: ff }}>
      {/* Header */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em' }}>Projected playoff bracket</div>
        <div style={{ fontSize: 13, color: '#86868B', marginTop: 2 }}>
          The most likely path to the title — percentages are how often each team reaches that round across all simulations.
        </div>
      </div>

      {/* Favorite banner */}
      {champ && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: 14, padding: '12px 16px', margin: '14px 0 18px' }}>
          <Avatar team={champ.team} size={34} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1D1D1F' }}>{champ.team.displayName}</div>
            <div style={{ fontSize: 12, color: '#6E6E73' }}>Title favorite — wins it in {pct1(champ.result.championshipProbability)} of seasons</div>
          </div>
        </div>
      )}

      {/* Bracket */}
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        {/* Column headers */}
        <div style={{ position: 'relative', height: 26, width: totalW, minWidth: totalW }}>
          {Array.from({ length: totalRounds + 1 }, (_, c) => {
            const h = headerFor(c);
            return (
              <div key={c} style={{ position: 'absolute', left: xFor(c), width: CARD_W, textAlign: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#C7C7CC' }}>{h.label}</span>
                {h.week ? <span style={{ fontSize: 9, fontWeight: 600, color: '#D2D2D7', marginLeft: 6 }}>WK {h.week}</span> : null}
              </div>
            );
          })}
        </div>

        {/* Bracket body */}
        <div style={{ position: 'relative', width: totalW, minWidth: totalW, height: totalH }}>
          {/* Connector lines */}
          <svg width={totalW} height={totalH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {connectors.map((d, i) => <path key={i} d={d} fill="none" stroke="#E0E0E3" strokeWidth={1.5} />)}
          </svg>

          {/* Column 0 — the seeded field (real matchups, with byes) */}
          {leaves.map((leaf, i) => {
            const seedNum = order[i];
            const isMe = leaf && leaf.team.rosterId === myRosterId;
            const y = leafY[i];
            if (!leaf) {
              return (
                <div key={i} style={{ position: 'absolute', left: xFor(0), top: y, width: CARD_W, height: CARD_H, borderRadius: 10, border: '1px dashed #E5E5EA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, letterSpacing: '1px', color: '#D2D2D7', textTransform: 'uppercase' }}>Bye</div>
              );
            }
            return (
              <div
                key={i}
                onClick={() => onSelectTeam && onSelectTeam(leaf.team.rosterId)}
                style={{
                  position: 'absolute', left: xFor(0), top: y, width: CARD_W, height: CARD_H,
                  display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', borderRadius: 10, cursor: 'pointer', boxSizing: 'border-box',
                  background: isMe ? 'rgba(255,149,0,0.10)' : '#fff',
                  border: isMe ? '1.5px solid rgba(255,149,0,0.5)' : '1px solid #ECECEE',
                }}
              >
                <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, background: seedNum <= 2 ? '#1D1D1F' : '#E5E5EA', color: seedNum <= 2 ? '#fff' : '#6E6E73', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{seedNum}</span>
                <Avatar team={leaf.team} size={22} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{leaf.team.displayName}</div>
                  <div style={{ fontSize: 9.5, color: '#86868B' }}>{pct(leaf.result.playoffProbability)} to make it</div>
                </div>
              </div>
            );
          })}

          {/* Internal columns — most likely occupant of each slot */}
          {Array.from({ length: totalRounds }, (_, ci) => ci + 1).map(c => (
            centerY[c].map((cy, k) => {
              const { picks, metaKey } = displayedFor(c, k);
              const top = cy - CARD_H / 2;
              const isChampCol = c === totalRounds;
              if (!picks.length) {
                return <div key={`${c}-${k}`} style={{ position: 'absolute', left: xFor(c), top, width: CARD_W, height: CARD_H, borderRadius: 10, border: '1px dashed #ECECEE' }} />;
              }
              const lead = picks[0];
              const next = picks[1];
              const isMe = lead.team.rosterId === myRosterId;
              const stage = isChampCol ? 'Chance to win it all' : (c === totalRounds - 1 ? 'Chance to reach the final' : 'Chance to reach the semis');
              return (
                <div
                  key={`${c}-${k}`}
                  onClick={() => onSelectTeam && onSelectTeam(lead.team.rosterId)}
                  onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setHover({ x: r.left, y: r.bottom + 8, stage, metaKey, picks }); }}
                  onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); setHover(h => h ? { ...h, x: r.left, y: r.bottom + 8 } : h); }}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    position: 'absolute', left: xFor(c), top, width: CARD_W, minHeight: CARD_H, boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 10, cursor: 'pointer',
                    background: isChampCol ? 'rgba(52,199,89,0.10)' : (isMe ? 'rgba(255,149,0,0.10)' : '#fff'),
                    border: isChampCol ? '1.5px solid rgba(52,199,89,0.45)' : (isMe ? '1.5px solid rgba(255,149,0,0.5)' : '1px solid #ECECEE'),
                  }}
                >
                  {isChampCol && <span style={{ fontSize: 16, flexShrink: 0 }}>🏆</span>}
                  <Avatar team={lead.team} size={24} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.team.displayName}</div>
                    <div style={{ fontSize: 9.5, color: isChampCol ? '#1a9640' : '#86868B', fontWeight: isChampCol ? 700 : 400 }}>
                      {pct(lead.result[metaKey])} {isChampCol ? 'to win it' : (c === totalRounds - 1 ? 'reach final' : 'reach semis')}
                    </div>
                    {next && !isChampCol && (
                      <div style={{ fontSize: 9, color: '#C7C7CC', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>or {next.team.displayName} {pct(next.result[metaKey])}</div>
                    )}
                  </div>
                </div>
              );
            })
          ))}
        </div>
      </div>

      {/* Outside the bracket */}
      {outside.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#C7C7CC', marginBottom: 8 }}>On the outside</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {outside.map((x, i) => {
              const isMe = x.team.rosterId === myRosterId;
              return (
                <div
                  key={x.team.rosterId}
                  onClick={() => onSelectTeam && onSelectTeam(x.team.rosterId)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px 7px 8px', borderRadius: 980, cursor: 'pointer', background: isMe ? 'rgba(255,149,0,0.10)' : '#F5F5F7', border: isMe ? '1px solid rgba(255,149,0,0.4)' : '1px solid transparent' }}
                >
                  <Avatar team={x.team} size={20} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1D1D1F' }}>{x.team.displayName}</span>
                  <span style={{ fontSize: 11, color: '#86868B' }}>{pct(x.result.playoffProbability)}</span>
                  {isMe && <span style={{ fontSize: 8, fontWeight: 700, background: '#FF9500', color: '#fff', borderRadius: 3, padding: '1px 4px' }}>YOU</span>}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: '#C7C7CC', marginTop: 10 }}>
            {outside[0].team.displayName} is the first team out — in the hunt at {pct(outside[0].result.playoffProbability)}.
          </div>
        </div>
      )}

      {/* Styled slot tooltip — every team that could fill this bracket spot, ranked */}
      {hover && (
        <div style={{
          position: 'fixed', top: hover.y, left: hover.x, zIndex: 200,
          background: '#1D1D1F', borderRadius: 12, padding: '11px 13px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)', minWidth: 200, maxWidth: 260,
          pointerEvents: 'none', fontFamily: ff,
        }}>
          <div style={{ position: 'absolute', top: -5, left: 22, width: 10, height: 10, background: '#1D1D1F', transform: 'rotate(45deg)', borderRadius: 2 }} />
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>{hover.stage}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {hover.picks.filter(p => (p.result[hover.metaKey] || 0) > 0.005).slice(0, 6).map(p => (
              <div key={p.team.rosterId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar team={p.team} size={18} />
                <span style={{ fontSize: 12, color: '#fff', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{p.team.displayName}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#7ee2a8', fontVariantNumeric: 'tabular-nums' }}>{pct1(p.result[hover.metaKey])}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
